import Image from 'next/image';

interface FooterProps {
  className?: string;
}

const Footer = ({ className = '' }: FooterProps) => {
  const navLinks = [
    { label: 'Solutions', href: 'https://wiliang.com/solutions' },
    { label: 'Outdoor', href: 'https://wiliang.com/outdoor-led-display/' },
    { label: 'Rental', href: 'https://wiliang.com/rentalled-screen' },
    { label: 'Contact Us', href: 'https://wiliang.com/contact-us' },
    { label: 'Blog', href: 'https://wiliang.com/wiliang-blog' },
  ];

  return (
    <footer className={`bg-primary text-white ${className}`}>
      <div className="container mx-auto px-5 md:px-10 py-12 md:py-16">
        <div className="grid md:grid-cols-3 gap-10 mb-12">
          {/* Logo & Description */}
          <div className="flex flex-col items-start">
            <div className="flex items-center gap-3 mb-6">
              <div className="relative w-40 h-12 rounded-lg overflow-hidden">
                <Image
                  src="/assets/images/wiliang_logo-1766495883797.png"
                  alt="Wiliang Logo"
                  fill
                  className="object-contain p-2 bg-white/10"
                />
              </div>
            </div>
            <p className="font-body text-base text-white/80 max-w-xs">
              Premium LED display solutions direct from our ISO-certified factory. Serving businesses worldwide with factory-direct pricing and exceptional quality from wiliang.com.
            </p>
          </div>

          {/* Navigation Links */}
          <div className="flex flex-col">
            <h3 className="font-heading text-lg font-semibold mb-5 text-white">Quick Links</h3>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-body text-base text-white/70 hover:text-white transition-colors duration-200"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Us */}
          <div className="flex flex-col">
            <h3 className="font-heading text-lg font-semibold mb-5 text-white">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-white/60 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a
                  href="mailto:info@wiliang.com"
                  className="font-body text-base text-white/70 hover:text-white transition-colors duration-200 break-all"
                >
                  info@wiliang.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-white/60 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                <a
                  href="https://wa.me/8619926825067"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-body text-base text-white/70 hover:text-white transition-colors duration-200"
                >
                  +86 19926825067
                </a>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-white/60 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="font-body text-base text-white/70 leading-relaxed">
                  3rd Floor, Building 7, Skyworth Innovation Valley, Baoan District, Shenzhen, China 518101
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/20 pt-8">
          <div className="flex flex-col items-center justify-center gap-4">
            <p className="font-body text-sm text-white/60 text-center">
              &copy; {new Date().getFullYear()} Wiliang. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;